import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { type ContentFrameworkTopic } from "@/lib/composerCore";

export type TopicChipProps = {
  topic: ContentFrameworkTopic;
  usageCount: number;
  reassignTargets: ContentFrameworkTopic[];
  showElaborations: boolean;
  onChange: () => void | Promise<void>;
};

export function TopicChip({ topic, usageCount, reassignTargets, showElaborations, onChange }: TopicChipProps) {
  const [label, setLabel] = useState(topic.label);
  const [description, setDescription] = useState(topic.description ?? "");
  const [editing, setEditing] = useState(false);

  const [renameOpen, setRenameOpen] = useState(false);
  const [pendingRename, setPendingRename] = useState<{ label: string; description: string } | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [reassignTargetId, setReassignTargetId] = useState<string>("");

  useEffect(() => {
    setLabel(topic.label);
    setDescription(topic.description ?? "");
  }, [topic.id, topic.label, topic.description]);

  const writeUpdate = async (nextLabel: string, nextDesc: string): Promise<boolean> => {
    const { error } = await supabase
      .from("content_framework_topics")
      .update({ label: nextLabel, description: nextDesc || null })
      .eq("id", topic.id);
    if (error) {
      toast.error(error.message);
      return false;
    }
    return true;
  };

  const save = async () => {
    const trimmed = label.trim();
    const trimmedDesc = description.trim();
    const labelChanged = trimmed && trimmed !== topic.label;
    const descChanged = trimmedDesc !== (topic.description ?? "");
    if (!trimmed) {
      setLabel(topic.label);
      setEditing(false);
      return;
    }
    if (!labelChanged && !descChanged) {
      setEditing(false);
      return;
    }
    if (labelChanged && usageCount > 0) {
      setPendingRename({ label: trimmed, description: trimmedDesc });
      setRenameOpen(true);
      return;
    }
    const ok = await writeUpdate(trimmed, trimmedDesc);
    if (!ok) return;
    setEditing(false);
    await onChange();
  };

  const confirmRenameEverywhere = async () => {
    if (!pendingRename) return;
    const ok = await writeUpdate(pendingRename.label, pendingRename.description);
    setRenameOpen(false);
    setPendingRename(null);
    if (!ok) return;
    setEditing(false);
    await onChange();
  };

  const confirmRenameAsNew = async () => {
    if (!pendingRename) return;
    const { error } = await supabase.from("content_framework_topics").insert({
      workspace_id: topic.workspace_id,
      framework_column_id: topic.framework_column_id,
      label: pendingRename.label,
      description: pendingRename.description || null,
      position: topic.position + 1,
    });
    setRenameOpen(false);
    setPendingRename(null);
    if (error) {
      toast.error(error.message);
      return;
    }
    setLabel(topic.label);
    setDescription(topic.description ?? "");
    setEditing(false);
    await onChange();
  };

  const saveDescriptionOnly = async () => {
    const trimmedDesc = description.trim();
    if (trimmedDesc === (topic.description ?? "")) return;
    const { error } = await supabase
      .from("content_framework_topics")
      .update({ description: trimmedDesc || null })
      .eq("id", topic.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    await onChange();
  };

  const performHardDelete = async () => {
    const { error } = await supabase.from("content_framework_topics").delete().eq("id", topic.id);
    setDeleteOpen(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    await onChange();
  };

  const performReassignAndDelete = async () => {
    if (!reassignTargetId) {
      toast.error("Pick a topic to reassign to");
      return;
    }
    const { error: updErr } = await supabase
      .from("excerpt_topics")
      .update({ topic_id: reassignTargetId })
      .eq("topic_id", topic.id);
    if (updErr) {
      toast.error(updErr.message);
      return;
    }
    await performHardDelete();
    setReassignTargetId("");
  };

  const remove = async () => {
    if (usageCount > 0) {
      setDeleteOpen(true);
      return;
    }
    await performHardDelete();
  };

  const renderUsageBadge = () => {
    if (usageCount <= 0) return null;
    return (
      <span
        className="ml-1.5 inline-flex items-center rounded-full bg-muted px-1.5 text-[10px] font-medium text-muted-foreground"
        title={`Used on ${usageCount} excerpt${usageCount === 1 ? "" : "s"}`}
      >
        {usageCount}
      </span>
    );
  };

  const renderRenameDialog = () => (
    <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename "{topic.label}"?</DialogTitle>
          <DialogDescription>
            This topic is used on {usageCount} excerpt{usageCount === 1 ? "" : "s"}.
            Renaming everywhere will update those assignments to the new label.
            Or you can keep this topic as-is and add the new label as a separate topic.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => { setRenameOpen(false); setPendingRename(null); }}>Cancel</Button>
          <Button variant="secondary" onClick={() => void confirmRenameAsNew()}>
            Create as new topic instead
          </Button>
          <Button onClick={() => void confirmRenameEverywhere()}>
            Rename everywhere
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const renderDeleteDialog = () => (
    <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete "{topic.label}"?</DialogTitle>
          <DialogDescription>
            This topic is used on {usageCount} excerpt{usageCount === 1 ? "" : "s"}.
            Deleting will unlink it from those excerpts. You can also reassign
            its assignments to another topic first.
          </DialogDescription>
        </DialogHeader>
        {reassignTargets.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
              Reassign to
            </p>
            <Select value={reassignTargetId} onValueChange={setReassignTargetId}>
              <SelectTrigger>
                <SelectValue placeholder="Pick a topic" />
              </SelectTrigger>
              <SelectContent>
                {reassignTargets.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => setDeleteOpen(false)}>Cancel</Button>
          {reassignTargets.length > 0 ? (
            <Button
              variant="secondary"
              onClick={() => void performReassignAndDelete()}
              disabled={!reassignTargetId}
            >
              Reassign, then delete
            </Button>
          ) : null}
          <Button variant="destructive" onClick={() => void performHardDelete()}>
            Delete and unlink {usageCount} excerpt{usageCount === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (editing) {
    return (
      <div className="space-y-1">
        <Input
          autoFocus
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          onBlur={() => { if (!showElaborations) void save(); }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void save();
            } else if (event.key === "Escape") {
              setLabel(topic.label);
              setDescription(topic.description ?? "");
              setEditing(false);
            }
          }}
        />
        {showElaborations ? (
          <Textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            onBlur={() => void save()}
            placeholder="Elaboration — context for the agents"
            rows={2}
          />
        ) : null}
        {renderRenameDialog()}
      </div>
    );
  }

  const hasElab = (topic.description ?? "").trim().length > 0;

  if (showElaborations) {
    return (
      <div className="group space-y-1 rounded-md border border-border bg-muted/30 p-2">
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            className="flex-1 truncate text-left text-sm"
            onClick={() => setEditing(true)}
          >
            {topic.label}
            {renderUsageBadge()}
          </button>
          <button
            type="button"
            onClick={() => void remove()}
            className="rounded-sm p-0.5 text-muted-foreground opacity-0 transition group-hover:opacity-100 hover:text-destructive"
            aria-label="Remove topic"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <Textarea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          onBlur={() => void saveDescriptionOnly()}
          placeholder="Elaboration — context for the agents"
          rows={2}
        />
        {renderRenameDialog()}
        {renderDeleteDialog()}
      </div>
    );
  }

  return (
    <div className="group flex items-center justify-between gap-2 rounded-md border border-border bg-muted/30 px-2 py-1">
      <button
        type="button"
        className="flex-1 truncate text-left text-sm"
        onClick={() => setEditing(true)}
      >
        {topic.label}
        {hasElab ? (
          <span
            className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-muted-foreground/60 align-middle"
            aria-label="Has elaboration"
            title="Has elaboration"
          />
        ) : null}
        {renderUsageBadge()}
      </button>
      <button
        type="button"
        onClick={() => void remove()}
        className="rounded-sm p-0.5 text-muted-foreground opacity-0 transition group-hover:opacity-100 hover:text-destructive"
        aria-label="Remove topic"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      {renderRenameDialog()}
      {renderDeleteDialog()}
    </div>
  );
}
